import { Clock, MapPinIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import {
  formatTimeRange,
  type ShowEntitiesAtLocationArgs,
  type ShowEntitiesAtLocationResult,
} from '@/features/agent/tools/show-entities-at-location'
import { useToolCardOpen } from '@/features/agent/tools/useToolCardOpen'
import { ToolCallCard, ToolCallHeader, type ToolCallStatus } from './_shared/ToolCallHeader'

export interface ShowEntitiesAtLocationViewProps {
  args: ShowEntitiesAtLocationArgs
  parsed: ShowEntitiesAtLocationResult | null
  running: boolean
  errored: boolean
  /** Resolved action footer, supplied by the connected tool. Pure stories
   *  pass a mock `<ToolCardActions>` here to render the footer. */
  actions?: ReactNode
  /** Force the card expanded even with no body. Defaults to false; the card
   *  auto-expands when it has results and stays collapsed for empty or failed
   *  calls. Stories pass true to render the expanded body. */
  defaultOpen?: boolean
}

/** Pure presentation for a `show_entities_at_location` tool-result card. */
export function ShowEntitiesAtLocationView({
  args,
  parsed,
  running,
  errored,
  actions,
  defaultOpen = false,
}: ShowEntitiesAtLocationViewProps) {
  const entities = parsed?.entities ?? []
  const hasBody = !running && (errored || parsed?.status === 'ok')
  const { open, onToggle } = useToolCardOpen(hasBody, defaultOpen, errored)

  if (!running && parsed?.status === 'empty') return null

  const locationName = parsed?.location?.name ?? args.location
  const entityCount = entities.length

  const headerStatus: ToolCallStatus = running ? 'running' : errored ? 'error' : 'complete'
  const description = running ? (
    <>
      Finding entities <span className="mx-1.5">·</span>
      <span className="text-foreground">{locationName}</span>
    </>
  ) : errored ? (
    'Failed'
  ) : (
    <>
      {entityCount === 1 ? '1 entity' : `${entityCount} entities`}
      <span className="mx-1.5">·</span>
      <span className="text-foreground">{locationName}</span>
    </>
  )

  return (
    <ToolCallCard>
      <ToolCallHeader
        status={headerStatus}
        icon={MapPinIcon}
        toolName="show_entities_at_location"
        description={description}
        expandable={hasBody}
        open={open}
        onToggle={onToggle}
      />

      {open && hasBody && (
        <div className="border-t border-border">
          {parsed?.status === 'error' && (
            <p className="whitespace-pre-wrap p-3 text-xs text-muted-foreground">
              {parsed.error ?? 'Unknown error'}
            </p>
          )}

          {parsed?.status === 'ok' && (
            <div className="flex flex-col">
              {parsed.time_range && (
                <div className="flex items-center gap-2 border-b border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                  <Clock className="size-3.5" />
                  <span>{formatTimeRange(parsed.time_range.start, parsed.time_range.end)}</span>
                </div>
              )}

              {entities.length > 0 ? (
                <ul className="divide-y divide-border">
                  {entities.slice(0, 10).map(entity => (
                    <li
                      key={entity.element_id}
                      className="flex items-start gap-2.5 px-3 py-2 text-xs"
                    >
                      <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-success" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium text-foreground">{entity.name}</p>
                      </div>
                      <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                        {entity.type}
                      </span>
                    </li>
                  ))}
                  {entities.length > 10 && (
                    <li className="px-3 py-2 text-xs text-muted-foreground">
                      +{entities.length - 10} more entities
                    </li>
                  )}
                </ul>
              ) : (
                <p className="p-3 text-xs text-muted-foreground">
                  No entities found at this location{parsed.time_range ? ' during this time' : ''}.
                </p>
              )}

              {entityCount > 0 && actions && (
                <div className="border-t border-border px-3 py-2">{actions}</div>
              )}
            </div>
          )}
        </div>
      )}
    </ToolCallCard>
  )
}
